import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../../components/ui/dialog';
import { apiErrorMessage } from '../utils/formatters';
import { useAuth } from '../../../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => {
  const n = Number(v) || 0;
  return `R ${n < 0 ? '-' : ''}${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Preview-then-fix for bad matches (backend: services/recon_cleanup.py):
 * matches pointing at a bank row that no longer exists, and Deal Room rows
 * that were matched automatically. Manual Deal Room matches are left alone.
 */
export function BadMatchesButton({ onDone }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setOpen(true);
    setBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investor-transactions/reconciliation/bad-matches`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Check failed'));
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const fix = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investor-transactions/reconciliation/bad-matches/undo`, {}, { headers });
      toast.success(`Released ${r.data.released} match(es) back to unmatched`);
      setOpen(false);
      setData(null);
      onDone && onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Clean-up failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-red-300 text-red-700 hover:bg-red-50"
        onClick={check}
        data-testid="recon-toolbar-bad-matches"
        title="Find matches whose bank transaction no longer exists, and Deal Room rows that were matched automatically, and put them back to unmatched."
      >
        <ShieldAlert className="h-4 w-4 mr-1" /> Clean Up Bad Matches
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex flex-col w-screen max-w-none h-[100dvh] rounded-none p-3 sm:p-6 sm:w-full sm:max-w-3xl sm:h-auto sm:max-h-[85vh] sm:rounded-lg">
          <DialogHeader className="text-left pr-8">
            <DialogTitle>Clean Up Bad Matches</DialogTitle>
            <DialogDescription>
              Puts these back to unmatched so you can match them properly. Matches you made by hand are not touched
              (except where the bank line no longer exists).
            </DialogDescription>
          </DialogHeader>
          {busy && !data ? (
            <div className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</div>
          ) : data && data.total === 0 ? (
            <p className="py-6 text-green-700">Nothing to clean up.</p>
          ) : data && (
            <>
              <div className="flex flex-wrap gap-3 text-sm">
                <span><strong>{data.bank_missing}</strong> matched to a bank row that no longer exists</span>
                <span><strong>{data.deal_room_automatic}</strong> Deal Room row(s) matched automatically</span>
                <span><strong>{data.wrong_investor_automatic || 0}</strong> auto-matched to a bank line that isn't that investor's</span>
                <span><strong>{data.bank_overclaimed_automatic || 0}</strong> sharing one bank line with another auto-matched row (the row that fits the bank line keeps it)</span>
              </div>
              <div className="flex-1 min-h-0 overflow-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 text-left">
                    <tr><th className="p-2">Investor</th><th className="p-2">Type</th><th className="p-2">Date</th><th className="p-2 text-right">Amount</th><th className="p-2">Why</th></tr>
                  </thead>
                  <tbody>
                    {data.rows.map(r => (
                      <tr key={`${r.collection}-${r.id}`} className="border-t">
                        <td className="p-2">{r.investor || '—'}</td>
                        <td className="p-2 whitespace-nowrap">{r.type || '—'}</td>
                        <td className="p-2 whitespace-nowrap">{r.date || '—'}</td>
                        <td className="p-2 text-right font-mono whitespace-nowrap">{r.amount != null ? fmt(r.amount) : '—'}</td>
                        <td className="p-2 text-xs">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end border-t pt-3">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={fix} disabled={busy} data-testid="recon-bad-matches-confirm">
                  {busy ? 'Working…' : `Release ${data.total} match(es)`}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BadMatchesButton;
